import { FileSyncOutlined } from '@ant-design/icons';
import type { ColumnDef } from '@tanstack/react-table';
import commaNumber from 'comma-number';
import { Check, CircleHelp, Copy, Sparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import Empty from '@/components/Empty/Empty';
import Modal from '@/components/Modal/Modal';
import { Stage } from '@/components/Stage/Stage';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import { Span } from '@/components/Typography/Typo';
import { JobStatus } from '@/features/Setting/Integrations/components/JobStatus/JobStatus';
import useModalState from '@/hooks/pure/useModalState';
import { DataTable } from '@/shared/ui';
import { toast } from '@/stores/pure/ToastStore';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { getFeedbackSourceName, getSourceIcon } from '@/types/feedbackConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { clipboardCopy } from '@/utils/util';

import type { ScrapingJob } from '../api/dto';
import { useScrapingJobDetailAction } from '../hooks/useScrapingJobDetailAction';
import ScrapingJobDetailModal from '../modals/ScrapingJobDetailModal';
import {
  isCancelableScrapingJob,
  isDeletableScrapingJob,
} from '../models/ScrapingJob';
import {
  getDisplayStatusByStatus,
  getSchedulePeriodText,
  SCRAPING_JOB_STATUS,
  SCRAPING_JOB_TYPE,
} from '../types';
import { FeedbackCountGuide } from './FeedbackCountGuide';

const DEEPBLUEDOT_EMAIL_DOMAIN = '@deepbluedot.io';
const COPY_FEEDBACK_DURATION_MS = 1000;

const ScrapingJobKeyCell = ({ alias }: { alias: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;

    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, COPY_FEEDBACK_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  const onCopyClick = () => {
    setCopied(true);
    clipboardCopy(alias);
    toast('Key copied to clipboard', 'success');
  };

  return (
    <div className="flex w-full min-w-0 max-w-[120px] items-center gap-1">
      <TooltipV2 tooltipNode={alias}>
        <Span type="label3" ellipsis className="min-w-0 flex-1">
          {alias}
        </Span>
      </TooltipV2>
      <Button
        variant="ghost_secondary"
        sizeType="xs"
        className="shrink-0"
        tooltip={copied ? 'Key copied' : 'Copy key'}
        aria-label={copied ? 'Key copied' : 'Copy key'}
        disabled={copied}
        onClick={onCopyClick}
      >
        <Stage className="flex size-3 items-center justify-center">
          <Stage.Scene key="copied" status={copied}>
            <Check className="size-3 text-green-700" />
          </Stage.Scene>
          <Stage.Scene key="copy" default>
            <Copy className="size-3" />
          </Stage.Scene>
        </Stage>
      </Button>
    </div>
  );
};

const ScrapingJobTitleCell = ({
  item,
  refetchCallback,
}: {
  item: ScrapingJob;
  refetchCallback: () => void;
}) => {
  const sourceName = getFeedbackSourceName(item.source_type);
  const sourceIcon = getSourceIcon(item.source_type, 16);
  const { onSummarizeWithHeySynclyClick, heySynclyEnabled } =
    useScrapingJobDetailAction(item, {
      refetchCallback,
      onConfirmCallback: (job_type: SCRAPING_JOB_TYPE) => {
        if (job_type === SCRAPING_JOB_TYPE.ONETIME) {
          toast('Job created successfully.', 'success');
        } else {
          toast('Scheduled created successfully.', 'success');
        }
      },
    });

  return (
    <div className="gap-2 flex items-center group relative w-full overflow-hidden">
      <TooltipV2 tooltipNode={sourceName}>
        <Flex className="size-4 shrink-0">{sourceIcon}</Flex>
      </TooltipV2>
      <TooltipV2 tooltipNode={item.title}>
        <Span type="label3" ellipsis className="flex-1">
          {item.title}
        </Span>
      </TooltipV2>
      <div className="absolute top-1/2 -translate-y-1/2 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white pl-2">
        <Button
          variant="outlined"
          sizeType="s"
          onClick={(e) => {
            e.stopPropagation();
            onSummarizeWithHeySynclyClick();
          }}
          disabled={!heySynclyEnabled}
          tooltip={
            heySynclyEnabled
              ? 'View insight with Hey syncly'
              : 'Hey syncly is only available for commerce and keyword base scraping jobs'
          }
        >
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Sparkles className="size-3" />
            Hey syncly
          </div>
        </Button>
      </div>
    </div>
  );
};

const ScrapingJobStatusCell = ({
  item,
  refetchCallback,
}: {
  item: ScrapingJob;
  refetchCallback: () => void;
}) => {
  const { onConfirmClick } = useScrapingJobDetailAction(item, {
    refetchCallback,
    onConfirmCallback: (job_type: SCRAPING_JOB_TYPE) => {
      if (job_type === SCRAPING_JOB_TYPE.ONETIME) {
        toast('Job created successfully.', 'success');
      } else {
        toast('Scheduled created successfully.', 'success');
      }
    },
  });

  if (item.status === SCRAPING_JOB_STATUS.BEFORE_CONFIRM) {
    return (
      <Button
        variant="primary"
        sizeType="m"
        onClick={(e) => {
          e.stopPropagation();
          void onConfirmClick();
        }}
      >
        Approve
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <JobStatus status={getDisplayStatusByStatus(item.status, item.type)} />
    </div>
  );
};

export const TaskTable = ({
  data,
  isLoading,
  refetch,
  refetchStats,
  selectedTab,
  onCreateClick,
  checkedList,
  onCheck,
}: {
  data: ScrapingJob[];
  isLoading: boolean;
  refetch: () => void;
  refetchStats: () => void;
  selectedTab: SCRAPING_JOB_TYPE;
  onCreateClick: () => void;
  checkedList: string[];
  onCheck: (list: string[]) => void;
}) => {
  const currentUserEmail = useUserStore((state) => state.user?.email);
  const members = useWorkspaceStore((state) => state.currWorkspaceMembers);
  const isDeepbluedotUser =
    currentUserEmail
      ?.trim()
      .toLowerCase()
      .endsWith(DEEPBLUEDOT_EMAIL_DOMAIN) ?? false;

  const {
    status: detailStatus,
    modalState: detailModalState,
    open: detailOpen,
    close: detailClose,
  } = useModalState<ScrapingJob>();

  const onDetailClick = (scrapingJob: ScrapingJob) => {
    detailOpen({
      modalState: scrapingJob,
    });
  };

  const refetchCallback = useCallback(() => {
    refetch();
    refetchStats();
  }, [refetch, refetchStats]);

  const selectableData = useMemo(() => {
    return data.filter(
      (item) => isCancelableScrapingJob(item) || isDeletableScrapingJob(item),
    );
  }, [data]);

  const isAllChecked =
    selectableData.length > 0 &&
    selectableData.every((item) => checkedList.includes(item.alias));
  const isSomeChecked =
    checkedList.length > 0 &&
    selectableData.some((item) => checkedList.includes(item.alias)) &&
    !isAllChecked;

  const columns = useMemo<ColumnDef<ScrapingJob>[]>(() => {
    const baseColumns: ColumnDef<ScrapingJob>[] = [
      {
        id: 'checkbox',
        header: () => (
          <Checkbox
            checked={isAllChecked}
            halfCheck={isSomeChecked}
            onChange={(checked) => {
              if (checked) {
                onCheck(selectableData.map((item) => item.alias));
              } else {
                onCheck([]);
              }
            }}
          />
        ),
        cell: ({ row }) => {
          const item = row.original;
          const isSelectable =
            isCancelableScrapingJob(item) || isDeletableScrapingJob(item);
          return (
            <Checkbox
              disabled={!isSelectable}
              checked={checkedList.includes(item.alias)}
              onChange={(status) =>
                onCheck(
                  status
                    ? [...checkedList, item.alias]
                    : checkedList.filter((alias) => alias !== item.alias),
                )
              }
            />
          );
        },
        size: 48,
        meta: { className: 'px-2' },
      },
    ];

    if (isDeepbluedotUser) {
      baseColumns.push({
        id: 'key',
        accessorKey: 'alias',
        header: 'Key',
        cell: ({ row }) => <ScrapingJobKeyCell alias={row.original.alias} />,
        size: 120,
        meta: { className: 'max-w-[120px]' },
      });
    }

    baseColumns.push({
      id: 'title',
      accessorKey: 'title',
      header: selectedTab === SCRAPING_JOB_TYPE.ONETIME ? 'Job' : 'Schedule',
      cell: ({ row }) => (
        <ScrapingJobTitleCell
          item={row.original}
          refetchCallback={refetchCallback}
        />
      ),
      meta: { className: 'flex-1 min-w-[180px]' },
    });

    if (selectedTab === SCRAPING_JOB_TYPE.ONETIME) {
      baseColumns.push({
        id: 'feedback-count',
        accessorKey: 'feedback_count',
        header: () => (
          <div className="w-full text-right pr-4">Feedback count</div>
        ),
        cell: ({ row }) => {
          const item = row.original;
          const hasDuplicated = item.collect_count > item.feedback_count;
          return (
            <div className="flex flex-col items-end gap-1 w-full pr-4">
              <div className="flex items-center gap-1.5">
                {hasDuplicated && (
                  <FeedbackCountGuide scrapingJob={item} align="end">
                    <CircleHelp className="size-3 text-gray-600" />
                  </FeedbackCountGuide>
                )}
                {commaNumber(item.collect_count)}
              </div>
            </div>
          );
        },
        size: 160,
      });
    } else {
      baseColumns.push({
        id: 'scheduled-date',
        header: 'Scheduled date',
        cell: ({ row }) => getSchedulePeriodText(row.original.schedule),
        size: 160,
      });
    }

    baseColumns.push({
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <ScrapingJobStatusCell
          item={row.original}
          refetchCallback={refetchCallback}
        />
      ),
      size: 180,
    });

    baseColumns.push({
      id: 'updated-by',
      accessorKey: 'updated_by',
      header: 'Requested by',
      cell: ({ row }) => {
        const item = row.original;
        const member = members.find((m) => m.user_id === item.updated_by);
        return (
          <TooltipV2 tooltipNode={member?.email || '-'}>
            <Span type="label3" ellipsis className="flex-1">
              {member?.first_name || '-'}
            </Span>
          </TooltipV2>
        );
      },
      size: 140,
    });

    if (selectedTab === SCRAPING_JOB_TYPE.ONETIME) {
      baseColumns.push({
        id: 'created-at',
        accessorKey: 'created_at',
        header: 'Created at',
        cell: ({ row }) =>
          getTimezoneDayjs(row.original.created_at).format(
            DATE_FORMAT.readable,
          ),
        size: 140,
      });
    } else {
      baseColumns.push({
        id: 'updated-at',
        accessorKey: 'updated_at',
        header: 'Updated at',
        cell: ({ row }) =>
          getTimezoneDayjs(row.original.updated_at).format(
            DATE_FORMAT.readable,
          ),
        size: 180,
      });
    }

    return baseColumns;
  }, [
    isAllChecked,
    isSomeChecked,
    isDeepbluedotUser,
    selectedTab,
    checkedList,
    members,
    onCheck,
    selectableData,
    refetchCallback,
  ]);

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        loading={isLoading}
        onClickRow={onDetailClick}
        emptyState={
          <Empty
            className="h-120"
            icon={<FileSyncOutlined />}
            title="Create request"
            description={
              selectedTab === SCRAPING_JOB_TYPE.ONETIME ? (
                <>
                  You haven’t requested any crawls.
                  <br />
                  Start gathering insights by requesting your first data crawl.
                </>
              ) : (
                <>
                  You don't have any scheduled jobs.
                  <br />
                  When you create recurring requests, they'll appear here.
                </>
              )
            }
            action={{
              onClick: onCreateClick,
              text: 'Create',
            }}
          />
        }
        className="min-h-[572px] h-fit flex-none"
      />
      <Modal
        status={detailStatus}
        closeModal={detailClose}
        useBackgroundClose
        useEscapeClose
      >
        <ScrapingJobDetailModal
          scrapingJob={detailModalState}
          refetchCallback={refetchCallback}
        />
      </Modal>
    </>
  );
};
